import { Layout, ConfigProvider } from "antd";
import { Navbar } from "Components/Navbar";
import { Content } from "antd/es/layout/layout";
import { RouterWrapper } from "Features/Routes/AllRoutes";
import "antd/dist/reset.css";
import "./App.css";
import { ErrorBoundary } from "Components/ErrorBoundary/ErrorBoundary";
import { useAxiosSetup } from "QueryClient/axiosClient";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "QueryClient/queryClient";
import ReactGA from "react-ga4";
import { getConfig } from "Config/config";
import { useEffect, useState } from "react";
import Sidebar from "Features/UserDashboard/Sidebar/Sidebar";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUserInfo } from "GlobalStates/User";
import { useLocation } from "react-router-dom";
import { AppProvider } from "Features/OGConnect/AppContext";
import { useRecoilValue } from "recoil";
import { cookieConsentState } from "GlobalStates/CookieConsent";
import { CookieBanner } from "Features/CookieBanner";

function App() {
  useAxiosSetup();
  const { user } = useUserInfo();
  const cookieConsent = useRecoilValue(cookieConsentState);
  const location = useLocation();
  const [navbarCollapsed, setNavbarCollapsed] = useState(false);
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (
      !cookieConsent ||
      !getConfig().GoogleAnalyticsTrackingId ||
      analyticsInitialized
    )
      return;
    ReactGA.initialize(getConfig().GoogleAnalyticsTrackingId);
    setAnalyticsInitialized(true);
  }, [cookieConsent, analyticsInitialized]);

  useEffect(() => {
    if (analyticsInitialized)
      ReactGA.send({ hitType: "pageview", page: window.location.pathname });
  }, [location, analyticsInitialized]);

  useEffect(() => {
    if (user?.id && analyticsInitialized) {
      ReactGA.set({ user_id: user?.id });
    }
  }, [user, analyticsInitialized]);

  useEffect(() => {
    const contentContainer = document.getElementById("content-container");
    if (contentContainer) {
      contentContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setIsScrolled(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const contentContainer = document.getElementById("content-container");
    if (contentContainer) {
      contentContainer.addEventListener("scroll", () => {
        setIsScrolled(contentContainer.scrollTop > 0);
      });
    }
  }, []);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <div className="App">
          <ErrorBoundary>
            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: "#80e280",
                  colorBgContainer: "#ffffff",
                  colorBgBase: "#ffffff",
                },
              }}
            >
              <AppProvider>
                <Layout
                  hasSider={user?.token ? true : false}
                  style={{
                    overflow: "initial",
                    width: "100%",
                    height: "100vh",
                  }}
                >
                  {user?.token && <Sidebar collapsed={navbarCollapsed} />}
                  <Layout
                    style={{
                      height: "100%",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    {!window.location.href.includes("processing") &&
                      !window.location.href.includes("verify-email") && (
                        <Navbar
                          collapsed={navbarCollapsed}
                          setCollapsed={setNavbarCollapsed}
                          isScrolled={isScrolled}
                        />
                      )}
                    <Content
                      id="content-container"
                      style={{
                        width: "100%",
                        height: "100%",
                        overflowY: "auto",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <RouterWrapper />
                    </Content>
                  </Layout>
                </Layout>
              </AppProvider>
              <CookieBanner />
            </ConfigProvider>
          </ErrorBoundary>
        </div>
      </QueryClientProvider>
      <ToastContainer />
    </>
  );
}

export default App;
